import { useAuthState } from '@/lib/services/auth/auth.query'
import type { AuthUser } from '@/lib/services/auth/auth.types'
import { useOrganization } from '@/lib/services/organizations/organizations.query'
import type { Organization } from '@/lib/services/organizations/organizations.types'
import { useParams } from '@tanstack/react-router'
import { createContext, useContext, type ReactNode } from 'react'

// Outside the provider the context is `{}`, so every field is optional.
export type OrganizationContextValue = {
  isImpersonating?: boolean
  organization?: Organization
  user?: AuthUser | null
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>({})

// The purpose of this context for global users is to track which organization
// is currently being impersonated (if any).
// For non-global users, the purpose of this context is to always contain the user's own organization,
// and only exists for consistency.
export function OrganizationContextProvider({ children }: { children?: ReactNode }) {
  // Route params are untyped while route files are JavaScript; narrow `$orgId` here.
  const params: unknown = useParams({ strict: false })
  const routeOrgId =
    typeof params === 'object' && params !== null && 'orgId' in params ? params.orgId : undefined
  const orgId =
    typeof routeOrgId === 'number' || typeof routeOrgId === 'string' ? routeOrgId : undefined
  const { data: organization } = useOrganization(orgId)
  const { data: { user } = { user: null } } = useAuthState()
  const isImpersonating = Boolean(
    user?.organization_id && orgId && Number(user.organization_id) !== Number(orgId)
  )

  return (
    <OrganizationContext.Provider value={{ isImpersonating, organization, user }}>
      {children}
    </OrganizationContext.Provider>
  )
}

export const useOrganizationContext = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext)

  if (context === undefined) {
    throw new Error('useOrganizationContext must be called within an OrganizationContext.Provider')
  }

  return context
}
